/**
 * generateYearDebriefPDF — 27/07/2026 (demande user).
 *
 * Export A4 du « Débrief de l'année » (Conseil d'administration) : KPIs de
 * l'exercice, comparaison inter-joueurs (percentiles) et frise mois par
 * mois des points de passage. Pensé pour être remis au responsable
 * pédagogique ou joint à un portfolio de compétences.
 */
import { jsPDF } from 'jspdf';
import { formatMoneyForPdf, normalizePdfSpaces } from './formatMoneyForPdf';
import { formatPlayTime } from './format';

const MONTHS_FR = ['', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

const TYPE_TAGS = {
  ao_won: { tag: 'AO GAGNÉ', color: [5, 150, 105] },
  ao_lost: { tag: 'AO PERDU', color: [107, 114, 128] },
  event: { tag: 'ÉVÉNEMENT', color: [217, 119, 6] },
  depot: { tag: 'DÉPÔT', color: [2, 132, 199] },
  fleet: { tag: 'FLOTTE', color: [79, 70, 229] },
  hr: { tag: 'RH', color: [147, 51, 234] },
};

const PCT_LABELS = [
  ['treasury', 'Trésorerie'],
  ['year_profit', 'Résultat net'],
  ['year_revenue', "Chiffre d'affaires"],
  ['fleet_total', 'Taille de flotte'],
  ['reputation', 'Réputation'],
];

export default function generateYearDebriefPDF({
  debrief, companyName, holderName, percentiles, sampleSize, download = true,
}) {
  if (!debrief) throw new Error('generateYearDebriefPDF: debrief is required');
  const k = debrief.kpis || {};
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const W = 210;
  const H = 297;
  const M = 15;

  const header = () => {
    doc.setFillColor(11, 61, 46);
    doc.rect(0, 0, W, 22, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(normalizePdfSpaces(`HST — Débrief annuel · Année ${debrief.year}`), M, 10);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(
      normalizePdfSpaces(`Édité le ${new Date().toLocaleDateString('fr-FR')} · Hyper Société de Transport`),
      M, 16,
    );
  };
  header();

  let y = 30;
  const pageBreak = (needed = 10) => {
    if (y + needed <= H - 15) return;
    doc.addPage();
    header();
    y = 30;
  };

  /* ── Identité ── */
  doc.setTextColor(31, 41, 55);
  doc.setFontSize(15);
  doc.setFont('helvetica', 'bold');
  doc.text(normalizePdfSpaces(companyName || 'Société HST'), M, y);
  y += 6;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(107, 114, 128);
  doc.text(normalizePdfSpaces([
    holderName && `Apprenant·e : ${holderName}`,
    `Exercice ${debrief.year} (jours ${(debrief.year - 1) * 360 + 1}–${debrief.year * 360})`,
    `Temps de jeu réel : ${formatPlayTime(k.playTimeSeconds || 0)}`,
  ].filter(Boolean).join(' · ')), M, y);
  y += 8;

  /* ── KPIs (grille 4 × 2) ── */
  doc.setDrawColor(229, 231, 235);
  doc.setLineWidth(0.2);
  doc.line(M, y, W - M, y);
  y += 4;
  const stats = [
    { label: "CA de l'année", value: formatMoneyForPdf(k.totalRevenue) },
    { label: 'Résultat net', value: `${(k.netResult || 0) >= 0 ? '+' : ''}${formatMoneyForPdf(k.netResult)}` },
    { label: 'Trésorerie fin d\'année', value: formatMoneyForPdf(k.treasuryEnd) },
    { label: 'Phase atteinte', value: `Phase ${k.phase || 1}` },
    { label: 'Flotte', value: `${k.fleetEnd || 0} véhicule(s)` },
    { label: 'Chauffeurs', value: `${k.driversEnd || 0}` },
    { label: 'AO remportés', value: `${k.aoWon || 0}` },
    { label: 'Contrats livrés', value: `${k.contractsCompleted || 0}` },
  ];
  const colW = (W - 2 * M) / 4;
  stats.forEach((stat, i) => {
    const x = M + (i % 4) * colW;
    const yy = y + Math.floor(i / 4) * 11;
    doc.setFontSize(7);
    doc.setTextColor(107, 114, 128);
    doc.text(normalizePdfSpaces(stat.label.toUpperCase()), x, yy);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(31, 41, 55);
    doc.text(normalizePdfSpaces(stat.value), x, yy + 4.5);
    doc.setFont('helvetica', 'normal');
  });
  y += 24;

  /* ── Comparaison inter-joueurs ── */
  doc.setFillColor(240, 253, 244);
  doc.setDrawColor(5, 150, 105);
  doc.setLineWidth(0.3);
  const pctLines = [];
  if (percentiles && (sampleSize || 0) >= 4) {
    PCT_LABELS.forEach(([key, label]) => {
      const p = percentiles[key];
      if (p === null || p === undefined) return;
      pctLines.push(`${label} : meilleur que ${p} % des joueurs`);
    });
    if (percentiles.pace !== null && percentiles.pace !== undefined) {
      pctLines.push(`Rythme : plus rapide que ${percentiles.pace} % des joueurs (temps réel de jeu)`);
    }
  }
  const pctBoxH = 10 + Math.max(1, Math.ceil(pctLines.length / 2)) * 5;
  doc.roundedRect(M, y, W - 2 * M, pctBoxH, 1.5, 1.5, 'FD');
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(6, 95, 70);
  doc.text(normalizePdfSpaces(`VOUS VS LES AUTRES JOUEURS — ANNÉE ${debrief.year}`), M + 4, y + 5.5);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(31, 41, 55);
  if (pctLines.length === 0) {
    doc.text(
      normalizePdfSpaces(`Comparaison disponible dès que 4 joueurs ou plus ont clôturé l'année ${debrief.year}.`),
      M + 4, y + 11,
    );
  } else {
    const half = (W - 2 * M) / 2;
    pctLines.forEach((line, i) => {
      const x = M + 4 + (i % 2) * half;
      const yy = y + 11 + Math.floor(i / 2) * 5;
      doc.text(normalizePdfSpaces(`• ${line}`), x, yy);
    });
  }
  y += pctBoxH + 8;

  /* ── Frise chronologique ── */
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(31, 56, 100);
  doc.text("POINTS DE PASSAGE DE L'ANNÉE", M, y);
  y += 6;

  const byMonth = {};
  (debrief.checkpoints || []).forEach(c => {
    (byMonth[c.monthIndex] = byMonth[c.monthIndex] || []).push(c);
  });

  (debrief.months || []).forEach((m, idx) => {
    const cps = byMonth[idx + 1] || [];
    pageBreak(8 + cps.length * 5);
    // Ligne mois + résultat
    doc.setFontSize(9.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(31, 41, 55);
    doc.text(normalizePdfSpaces(MONTHS_FR[m.month] || `Mois ${m.month}`), M, y);
    const net = m.netResult || 0;
    doc.setTextColor(...(net >= 0 ? [5, 150, 105] : [220, 38, 38]));
    doc.text(
      normalizePdfSpaces(`${net >= 0 ? '+' : ''}${formatMoneyForPdf(net)}`),
      W - M, y, { align: 'right' },
    );
    doc.setDrawColor(229, 231, 235);
    doc.setLineWidth(0.2);
    doc.line(M, y + 1.5, W - M, y + 1.5);
    y += 6;
    doc.setFont('helvetica', 'normal');
    if (cps.length === 0) {
      doc.setFontSize(8);
      doc.setTextColor(156, 163, 175);
      doc.text('Exploitation courante, rien à signaler.', M + 4, y);
      y += 5;
    } else {
      cps.forEach(c => {
        pageBreak(6);
        const t = TYPE_TAGS[c.type] || TYPE_TAGS.event;
        doc.setFontSize(6.5);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...t.color);
        doc.text(normalizePdfSpaces(t.tag), M + 4, y);
        doc.setFontSize(8.5);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(31, 41, 55);
        let label = c.label || '';
        if (c.impact) {
          label += `  (${c.impact > 0 ? '+' : ''}${formatMoneyForPdf(c.impact)})`;
        }
        doc.text(normalizePdfSpaces(label), M + 26, y);
        y += 5;
      });
    }
    y += 2;
  });

  /* ── Pied de page ── */
  pageBreak(12);
  doc.setFontSize(7.5);
  doc.setTextColor(156, 163, 175);
  doc.text(
    normalizePdfSpaces('Document généré par HST — Hyper Société de Transport. À remettre au responsable pédagogique ou à joindre à un portfolio de compétences.'),
    M, H - 10,
  );

  const filename = `HST-debrief-annee-${debrief.year}${companyName ? `-${companyName.replace(/[^a-z0-9]+/gi, '-')}` : ''}.pdf`;
  if (download) {
    doc.save(filename);
    return undefined;
  }
  return doc.output('blob');
}
